/**
 * IncidentManagementSection (SITL surface — operator-cop-doctrine §2.4, PRD
 * §4.19.1/§4.19.2). The operator-visible face of the API-only incident →
 * operational-period → SITREP-chain model: it makes the Situation Unit Leader's
 * planning-cycle clock workable from the COP.
 *
 * Per the design spec (docs/incident-period-ui-spec.md) it is NOT a new
 * top-level region — it renders inside the existing 380px right-rail detail
 * panel (SidePanel), on any body that resolves to a tracked incident
 * (FireBody / HighRiskHotspotBody / CommunityBody). Three stacked blocks:
 *   A. Incident header   — number · hazard badge · open/closed · owned · Deactivate
 *   B. Op-period strip    — current-period card · history stepper · Declare Next
 *   C. Report chain       — Initial / Update N / Final, auto-vs-operator, →viewer
 *
 * Register (§3A.3): predominantly ambient record-keeping; the act register is
 * spent only on the consequential live controls (Deactivate Incident, Declare Next
 * Period, the forward transition). Motion budget (§3A.5): spends nothing.
 * Audit (§3A.4): every write maps to an existing verb, server-stamped
 * (close_incident / declare_operational_period / transition_operational_period /
 * set_operational_period_cadence); the report viewer logs view_sitrep on mount.
 * No new verb, no migration.
 */
import { useCallback, useContext, useEffect, useMemo, useState, type JSX } from 'react';
import type {
  AssistanceRequest,
  IncidentDetail,
  IncidentLifecycleState,
  IncidentResponseLevel,
  IncidentSitrepRef,
  OperationalPeriod,
} from '@tsr/shared-types';
import { nextLifecycleState, prevLifecycleState, responseLevelActionWord } from '@tsr/shared-types';
import { AuthContext } from '../auth/AuthContext';
import {
  acknowledgeIncidentTransfer,
  advanceIncidentLifecycle,
  assumeIncidentCommand,
  clearIncidentResponseLevel,
  closeIncident,
  createSitrep,
  declareOperationalPeriod,
  fetchIncidentAssistanceRequests,
  fetchIncidentDetail,
  inviteToUnifiedCommand,
  releaseIncidentCommand,
  removeFromAreaCommand,
  setAreaCommandObjectives,
  setAreaCommandResourcePriorities,
  setIncidentResponseLevel,
  setOperationalPeriodCadence,
  transferIncidentCommand,
  transitionOperationalPeriod,
} from '../api';
import { ResponseLevelBadge } from '../ResponseLevelBadge';
import { RESPONSE_LEVEL_META, THREAT_LEVEL_META, isResponseLevel } from '../styles/responseLevel';
import { getSessionId } from '../lib/operatorActions';
import { AfterActionReportPanel } from '../AfterActionReportPanel';
import {
  ASSUME_COMMAND_LABEL,
  RELEASE_COMMAND_LABEL,
  INVITE_TO_UNIFIED_COMMAND_LABEL,
  ACCEPT_UNIFIED_COMMAND_LABEL,
  TRANSFER_OF_COMMAND_LABEL,
  RETURN_TO_SINGLE_COMMAND_LABEL,
  ACKNOWLEDGE_TRANSFER_LABEL,
  AREA_COMMAND_ESTABLISHED_PREFIX,
  AREA_COMMAND_HEADING,
  REMOVE_FROM_AREA_COMMAND_LABEL,
  underAreaCommandLabel,
  COORDINATED_BY_LABEL,
  REACTIVATE_INCIDENT_LABEL,
  REACTIVATION_STALENESS_WARNING_DAYS,
  reactivatedGapLabel,
  staleReactivationWarning,
} from '../incidentVocabulary';
import { AreaCommandChildrenSection } from './AreaCommandChildrenSection';
import { AreaCommandDirectivesSection } from './AreaCommandDirectivesSection';
import {
  IncidentCommandBlock,
  AssumeCommandForm,
  InviteToUnifiedCommandForm,
  TransferOfCommandForm,
} from './IncidentCommandBlock';
import { LifecycleStepper, type LifecycleStep } from './LifecycleStepper';
import {
  hazardStyle,
  incidentLifecycleStyle,
  incidentStatusStyle,
  periodStatusStyle,
  type BadgeStyle,
} from '../styles/incidentStyles';
import { ConnectedEvacOrders } from '../ConnectedEvacOrders';
import { IncidentCorroborationChip } from './IncidentCorroborationChip';
import { PublicInfoDraft } from './PublicInfoDraft';
import { EmergencyDeclarationBlock } from './EmergencyDeclarationBlock';
import { PublicAlertRequestBlock } from './PublicAlertRequestBlock';
import { ResidentPublicInfoBlock } from './ResidentPublicInfoBlock';
import { DeskWatchControl } from './DeskWatchControl';
import { ContributingHazardsSection } from './ContributingHazardsSection';

/* ------------------------------------------------------------------ *
 * Small presentational helpers
 * ------------------------------------------------------------------ */

/**
 * #485 SIDE-EFFECT CUES after a reactivation. Component-local (not in incidentVocabulary.ts): the
 * "operational period" cue would trip that module's Area-Command drift guard, and these cues render
 * only on a normal reopened incident where the term is correct doctrine. The operator Response
 * Level, operational periods, and Incident Command are all preserved-but-not-auto-resumed on reopen.
 */
const REACTIVATED_FOLLOWUP_HEADING = 'After reactivating';
const REACTIVATED_OPEN_PERIOD_CUE = 'Open a new operational period to resume the SITREP chain.';
const REACTIVATED_REASSUME_COMMAND_CUE =
  'Re-assume Incident Command — command is not restored automatically.';
const REACTIVATED_RESPONSE_LEVEL_CUE =
  'Re-confirm the Response Level — the assessment predates this reactivation and may be stale.';

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const past = diffMs >= 0;
  const s = Math.abs(diffMs) / 1000;
  const mins = Math.round(s / 60);
  const hrs = Math.round(s / 3600);
  const days = Math.round(s / 86400);
  let body: string;
  if (s < 60) body = 'just now';
  else if (mins < 60) body = `${mins}m`;
  else if (hrs < 48) body = `${hrs}h`;
  else body = `${days}d`;
  if (body === 'just now') return body;
  return past ? `${body} ago` : `in ${body}`;
}

function absTime(iso: string): string {
  return new Date(iso).toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

/** A colour+icon+label pill (hue never load-bearing alone). */
function Pill({ style, title, testid }: { style: BadgeStyle; title?: string; testid?: string }): JSX.Element {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium"
      style={{ backgroundColor: `${style.color}33`, color: style.color }}
      title={title}
    >
      <span aria-hidden>{style.icon}</span>
      {style.label}
    </span>
  );
}

/** "Default (midpoint + final)" vs "Update every N min". */
function cadenceLabel(mins: number | null): string {
  return mins == null ? 'Default · midpoint + final' : `Update every ${mins} min`;
}

const CADENCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Default — midpoint + final' },
  { value: '15', label: 'Update every 15 min' },
  { value: '30', label: 'Update every 30 min' },
  { value: '60', label: 'Update every 60 min' },
  { value: '120', label: 'Update every 120 min' },
];

/** datetime-local value (YYYY-MM-DDTHH:mm) for a Date in the browser's locale. */
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ------------------------------------------------------------------ *
 * Report chain
 * ------------------------------------------------------------------ */

/** Label a chain ref as Initial / Update N / Final / Standalone. `updateIndex`
 *  is the 1-based ordinal among updates (so the operator reads "Update 2", not a
 *  raw overall sequence). */
function reportLabel(ref: IncidentSitrepRef, updateIndex: number): string {
  switch (ref.report_version) {
    case 'initial':
      return 'Initial';
    case 'final':
      return 'Final';
    case 'update':
      return `Update ${updateIndex}`;
    default:
      return 'Standalone';
  }
}

function ReportChainRow({
  report: r,
  label,
  onOpen,
}: {
  report: IncidentSitrepRef;
  label: string;
  onOpen?: (id: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={`report-chain-row-${r.id}`}
      onClick={() => onOpen?.(r.id)}
      disabled={!onOpen}
      className="w-full flex items-center gap-2 py-1.5 px-2 rounded text-left hover:bg-slate-800/60 disabled:hover:bg-transparent"
      title={`${r.title} · ${absTime(r.created_at)}`}
    >
      <span className="text-xs font-medium text-slate-200 shrink-0">{label}</span>
      <span
        data-testid={`report-origin-${r.id}`}
        className={`text-[9px] px-1 py-px rounded shrink-0 ${
          r.auto_generated
            ? 'bg-slate-700/70 text-slate-300'
            : 'bg-sky-900/50 text-sky-300'
        }`}
        title={r.auto_generated ? 'Issued by the scheduler' : 'Issued by an operator'}
      >
        {r.auto_generated ? '⚙ auto' : '◑ operator'}
      </span>
      <span className="text-[10px] text-slate-500 ml-auto shrink-0">{relTime(r.created_at)}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Declare-next-period inline form
 * ------------------------------------------------------------------ */

function DeclarePeriodForm({
  defaultStart,
  busy,
  onSubmit,
  onCancel,
}: {
  defaultStart: Date;
  busy: boolean;
  onSubmit: (startIso: string, endIso: string, cadence: number | null) => void;
  onCancel: () => void;
}): JSX.Element {
  const [start, setStart] = useState(() => toLocalInput(defaultStart));
  const [end, setEnd] = useState(() => toLocalInput(new Date(defaultStart.getTime() + 24 * 3600 * 1000)));
  const [cadence, setCadence] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = (): void => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      setErr('Enter valid start and end times.');
      return;
    }
    if (endDate <= startDate) {
      setErr('End must be after start.');
      return;
    }
    setErr(null);
    onSubmit(startDate.toISOString(), endDate.toISOString(), cadence === '' ? null : Number(cadence));
  };

  const fieldCls =
    'w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200';

  return (
    <div data-testid="declare-period-form" className="space-y-2 rounded border border-slate-700 bg-slate-900/50 p-2">
      <label className="block text-[10px] uppercase tracking-wider text-slate-400">
        Start
        <input type="datetime-local" className={fieldCls} value={start} onChange={(e) => setStart(e.target.value)} />
      </label>
      <label className="block text-[10px] uppercase tracking-wider text-slate-400">
        End
        <input type="datetime-local" className={fieldCls} value={end} onChange={(e) => setEnd(e.target.value)} />
      </label>
      <label className="block text-[10px] uppercase tracking-wider text-slate-400">
        SITREP cadence (v3.3)
        <select
          data-testid="declare-period-cadence"
          className={fieldCls}
          value={cadence}
          onChange={(e) => setCadence(e.target.value)}
        >
          {CADENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {err && <p className="text-[10px] text-rose-400">{err}</p>}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          data-testid="declare-period-submit"
          onClick={submit}
          disabled={busy}
          className="flex-1 py-1.5 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-medium"
        >
          {busy ? 'Opening…' : 'Open period'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="py-1.5 px-3 rounded border border-slate-600 text-slate-300 text-xs hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * #445-ws2 — the incident Response Level: honesty display + IC control
 * ------------------------------------------------------------------ */

/**
 * The incident's Response Level (Ontario IMS §5 Table 1). Shows BOTH the OPERATIVE
 * (effective) level the IC asserts AND the SYSTEM-DERIVED level (annotated, never
 * overwritten — the #398 single-column anti-pattern is deliberately not copied); flags the
 * de-escalation divergence when the derived exceeds the pin; and, once command is
 * established, lets the Incident Commander Set / Escalate / De-escalate (§6.1 authority —
 * records-not-confers, NEVER "Declare"). Verbatim RESPONSE_LEVEL_META vocabulary
 * (Small/Large/Major/Emergency).
 */
export function IncidentResponseLevelBlock({
  rl,
  hasCommand,
  canControl = true,
  incidentId,
  sessionId,
  busy,
  run,
}: {
  rl: IncidentResponseLevel;
  hasCommand: boolean;
  /** ★ Slice-5 owner gate — false hides the IC's Set/Escalate/De-escalate CONTROL for a non-owner
   *  while STILL rendering the level READ (the shared picture). Defaults true (back-compat). */
  canControl?: boolean;
  incidentId: string;
  sessionId: string;
  busy: boolean;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  // Nothing to render when there is neither a level to show nor command to set one.
  if (rl.effective == null && !hasCommand) return null;

  const hasPin = rl.operator_set != null;
  const actionWord = picked != null ? responseLevelActionWord(rl.operator_set, picked) : 'Set';
  const canSubmit = !busy && picked != null && reason.trim() !== '';
  const submit = (): void => {
    if (picked == null || reason.trim() === '') return;
    void run(() => setIncidentResponseLevel(incidentId, picked, reason.trim(), sessionId)).then(() => {
      setOpen(false);
      setPicked(null);
      setReason('');
    });
  };

  return (
    <div
      className="mt-2 rounded border border-slate-700/60 bg-slate-900/40 px-2.5 py-2"
      data-testid="incident-response-level"
    >
      <p className="text-[10px] uppercase tracking-wider text-slate-500">Response Level</p>
      {/* Operative (effective) level = the IC pin over the derived. */}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {isResponseLevel(rl.effective) ? (
          <ResponseLevelBadge level={rl.effective} testid="incident-response-level-effective" />
        ) : (
          <span className="text-[11px] text-slate-500" data-testid="incident-response-level-none">
            Response Level not yet assessed
          </span>
        )}
        {hasPin && (
          <span
            className="text-[11px] text-slate-400"
            data-testid="incident-response-level-attribution"
          >
            assessed by {rl.assessed_by ?? 'the Incident Commander'}
            {rl.assessed_at ? (
              <>
                {' · '}
                <span title={absTime(rl.assessed_at)}>{relTime(rl.assessed_at)}</span>
              </>
            ) : null}
          </span>
        )}
      </div>
      {hasPin && rl.reason && (
        <p
          className="mt-1 text-[11px] italic text-slate-400"
          data-testid="incident-response-level-reason"
        >
          “{rl.reason}”
        </p>
      )}
      {/* The SYSTEM-DERIVED level — ALWAYS annotated when a pin exists (never overwritten).
          #613 — a system DERIVES a THREAT, not a §5 response scale; the OPERATIVE badge above
          stays the IC-assessed §5 Response Level (this incident is owned). */}
      {hasPin && (
        <p className="mt-1 text-[11px] text-slate-500" data-testid="incident-response-level-derived">
          {isResponseLevel(rl.derived)
            ? `system-derived Threat Level ${rl.derived} · ${THREAT_LEVEL_META[rl.derived].name}`
            : 'no system-derived Threat Level (no scored hazard)'}
        </p>
      )}
      {/* DE-ESCALATION GUARD — a worsening derived the pin now sits below MUST surface.
          #613 — the machine THREAT now exceeds the IC's assessed §5 RESPONSE level. */}
      {rl.derived_exceeds_operator && isResponseLevel(rl.derived) && isResponseLevel(rl.operator_set) && (
        <p
          className="mt-1 rounded border border-red-900/60 bg-red-950/40 px-2 py-1 text-[11px] font-semibold text-red-300"
          data-testid="incident-response-level-divergence"
        >
          ⚠ System-derived Threat Level {rl.derived} now exceeds the assessed Response Level{' '}
          {rl.operator_set} — re-assess.
        </p>
      )}
      {/* The IC control — Set / Escalate / De-escalate (§6.1), gated to established command AND to
          the OWNER (§4.1 — a non-owner reads the level but cannot assess it). */}
      {hasCommand && canControl && (
        <div className="mt-2" data-testid="incident-response-level-control">
          {!open ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(true);
                setPicked(rl.effective ?? null);
              }}
              className="rounded border border-slate-600 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700/40 disabled:opacity-50"
              data-testid="incident-response-level-open"
            >
              {hasPin ? 'Re-assess Response Level' : 'Set Response Level'}
            </button>
          ) : (
            <div className="space-y-1.5">
              <div className="flex flex-wrap gap-1" role="group" aria-label="Response Level">
                {([1, 2, 3, 4] as const).map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    disabled={busy}
                    aria-pressed={picked === lvl}
                    onClick={() => setPicked(lvl)}
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${picked === lvl ? 'ring-1 ring-white/70' : ''}`}
                    style={{
                      backgroundColor: `${RESPONSE_LEVEL_META[lvl].color}33`,
                      color: RESPONSE_LEVEL_META[lvl].color,
                    }}
                    data-testid={`incident-response-level-pick-${lvl}`}
                  >
                    {lvl} · {RESPONSE_LEVEL_META[lvl].name}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Assessment reason (required)"
                maxLength={500}
                className="w-full rounded border border-slate-600 bg-slate-950/60 px-2 py-1 text-[11px] text-slate-100 placeholder:text-slate-500"
                data-testid="incident-response-level-reason-input"
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={submit}
                  className="rounded bg-sky-700 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-sky-600 disabled:opacity-40"
                  data-testid="incident-response-level-submit"
                >
                  {busy ? 'Saving…' : `${actionWord} Response Level`}
                </button>
                {hasPin && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void run(() => clearIncidentResponseLevel(incidentId, sessionId)).then(() =>
                        setOpen(false),
                      )
                    }
                    className="rounded border border-slate-600 px-2 py-0.5 text-[11px] text-slate-300 hover:bg-slate-700/40 disabled:opacity-50"
                    data-testid="incident-response-level-clear"
                  >
                    Clear (revert to system-derived)
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setOpen(false);
                    setPicked(null);
                    setReason('');
                  }}
                  className="text-[11px] text-slate-500 hover:text-slate-300"
                  data-testid="incident-response-level-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Main section
 * ------------------------------------------------------------------ */

export function IncidentManagementSection({
  incidentId,
  onOpenSitrep,
  onOpenReplay,
  replayActive = false,
  onDetailLoaded,
}: {
  incidentId: string;
  /** Open a SITREP from the chain in the existing 720px document modal (§3B.4). */
  onOpenSitrep?: (id: string) => void;
  /** #180 — enter the AAR time replay: scrub this incident's recorded audit
   *  trail + anomaly lifecycle on the map (read-only, NOT-LIVE chrome). */
  onOpenReplay?: (incidentId: string) => void;
  /** #180 — replay already open (disables re-entry). */
  replayActive?: boolean;
  /** Phase 2 — fires each time the incident detail loads, so a parent (the work-
   *  surface) can derive incident-scoped UI (e.g. the Evacuation gate's linked-
   *  community count) from THIS single fetch instead of a duplicate round-trip. */
  onDetailLoaded?: (detail: IncidentDetail) => void;
}): JSX.Element | null {
  // ★ Slice-5 owner gate — the viewer's acting agency (null under AUTH-off / when a test omits the
  // provider — the null-tolerant read never throws). Read at the TOP so the hook order is stable.
  const viewerAgencyId = useContext(AuthContext)?.user?.agency?.id ?? null;
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  // #425 LINK — assistance requests attached to this incident (read-only read-back).
  const [linkedRequests, setLinkedRequests] = useState<AssistanceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [declaring, setDeclaring] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // R1/#477 — the pending lifecycle transition awaiting the two-step confirm (null = idle).
  const [confirmLifecycle, setConfirmLifecycle] = useState<{
    to: IncidentLifecycleState;
    direction: 'advance' | 'revert';
  } | null>(null);
  // W3 spine (#489) — the assume-command form (confirmed→owned) + release confirm (owned→confirmed).
  const [assumeOpen, setAssumeOpen] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  // W3 unified command + transfer (#492) — the enter-unified + transfer-of-command forms.
  const [unifiedOpen, setUnifiedOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    void fetchIncidentDetail(incidentId, ctl.signal)
      .then((d) => {
        setDetail(d);
        setLoading(false);
        onDetailLoaded?.(d);
      })
      .catch((e: unknown) => {
        if (!ctl.signal.aborted) {
          setErr(e instanceof Error ? e.message : 'Failed to load incident');
          setLoading(false);
        }
      });
    return () => ctl.abort();
  }, [incidentId, reload, onDetailLoaded]);

  // #425 LINK — the requests attached to this incident + their down-tasks (read-only).
  useEffect(() => {
    const ctl = new AbortController();
    void fetchIncidentAssistanceRequests(incidentId, ctl.signal)
      .then(setLinkedRequests)
      .catch(() => {
        /* read-back is best-effort; never blocks the incident view */
      });
    return () => ctl.abort();
  }, [incidentId, reload]);

  // R5 Slice 3 (#507, adversarial-verify finding) — transient form/confirm state is
  // PER-INCIDENT: the SidePanel mounts this section UNKEYED, so without this reset a
  // stale open form (e.g. assumeOpen from a normal incident) survives a switch to a
  // DIFFERENT incident — including onto an Area Command parent, where a person-field
  // form must never render (App A #2, #498).
  useEffect(() => {
    setAssumeOpen(false);
    setConfirmRelease(false);
    setUnifiedOpen(false);
    setTransferOpen(false);
    setConfirmClose(false);
    setConfirmLifecycle(null);
    setDeclaring(false);
  }, [incidentId]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  const run = useCallback(
    async (fn: () => Promise<unknown>): Promise<void> => {
      setBusy(true);
      setErr(null);
      try {
        await fn();
        refresh();
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Action failed');
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  // R5 Slice 4 (#514) — generate the Area-Command-scoped SITREP from the parent (the
  // aggregate roll-up of the grouped incidents) and open it in the shared SITREP viewer.
  // Rides the existing 'incident' scope with the area_command discriminator (no migration).
  const generateAreaCommandSitrep = useCallback(() => {
    if (!detail || busy) return;
    setBusy(true);
    setErr(null);
    void createSitrep({
      scope_type: 'incident',
      scope_payload: { incident_id: detail.id, incident_type: 'area_command' },
      hazard_focus: 'multi-hazard',
      session_id: getSessionId(),
    })
      .then((report) => onOpenSitrep?.(report.id))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Could not generate the SITREP.'))
      .finally(() => setBusy(false));
  }, [detail, busy, onOpenSitrep]);

  // Stable reference so the dependent useMemos don't recompute every render.
  const periods = useMemo<OperationalPeriod[]>(
    () => detail?.operational_periods ?? [],
    [detail?.operational_periods],
  );

  // The period to surface as the "current" card: the active one, else the latest
  // planned (the next to run), else the latest period overall.
  const currentPeriod = useMemo<OperationalPeriod | null>(() => {
    if (!periods.length) return null;
    return (
      periods.find((p) => p.status === 'active') ??
      [...periods].reverse().find((p) => p.status === 'planned') ??
      periods[periods.length - 1] ??
      null
    );
  }, [periods]);

  // History stepper: each period a step; closed→complete, active→current,
  // planned→future. Reuses the §3B.5 LifecycleStepper grammar verbatim.
  const periodSteps = useMemo<LifecycleStep[]>(
    () =>
      periods.map((p) => ({
        label: `P${p.period_number}`,
        status: p.status === 'closed' ? 'complete' : p.status === 'active' ? 'current' : 'future',
        detail: `${absTime(p.start_at)} → ${absTime(p.end_at)} · ${cadenceLabel(p.sitrep_cadence_minutes)}`,
      })),
    [periods],
  );

  // Report chain rows with computed Update ordinals (1-based among updates, so
  // the operator reads "Update 2" rather than a raw overall sequence).
  const chainRows = useMemo(() => {
    let updateOrdinal = 0;
    return (detail?.sitreps ?? []).map((r) => {
      if (r.report_version === 'update') updateOrdinal += 1;
      return { ref: r, label: reportLabel(r, updateOrdinal) };
    });
  }, [detail?.sitreps]);

  if (loading && !detail) {
    return (
      <section data-testid="incident-management-skeleton" className="space-y-2 animate-pulse">
        <div className="h-4 w-2/3 rounded bg-slate-800" />
        <div className="h-12 rounded bg-slate-800/70" />
        <div className="h-6 rounded bg-slate-800/50" />
        <div className="h-6 rounded bg-slate-800/50" />
      </section>
    );
  }
  if (!detail) return null;

  const sessionId = getSessionId();
  const hStyle = hazardStyle(detail.hazard_type);
  const sStyle = incidentStatusStyle(detail.status);
  const isOpen = detail.status === 'open';
  // R1/#477 — the wildfire lifecycle phase + its legal neighbours (the controls derive
  // from INCIDENT_LIFECYCLE_TRANSITIONS via next/prev so they can't diverge from the gate).
  const lc = detail.lifecycle_state;
  const lcStyle = incidentLifecycleStyle(lc);
  const lcNext = nextLifecycleState(lc);
  const lcPrev = prevLifecycleState(lc);
  // #485 — the revert off 'deactivated' is a REACTIVATE (the "Deactivated" antonym), never the
  // fire-only "Reignite" #535 shipped (wrong for flood/ice). Backend `reignited` token unchanged.
  const isReactivate = lc === 'deactivated';
  // #485 STALENESS (MUST-3) — while deactivated, closed_at IS the deactivation instant, so age
  // needs no ledger. A reactivation older than the founder threshold gets a WARNING (operator
  // judgment governs — a genuine re-emergence can be months later), not a hard block.
  const daysSinceDeactivation =
    isReactivate && detail.closed_at
      ? Math.floor((Date.now() - new Date(detail.closed_at).getTime()) / 86_400_000)
      : 0;
  const isStaleReactivation =
    isReactivate && daysSinceDeactivation > REACTIVATION_STALENESS_WARNING_DAYS;
  // #485 HONESTY (MUST-2) — the latest completed deactivation→reactivation cycle, derived from
  // the audit ledger (closed_at is NULL after reopen). Drives the header "Reactivated · was
  // deactivated" line so a reopened incident never reads continuously-active.
  // Tolerant of a mock/older detail that predates the field (the live API always sends it).
  const latestReactivation = (detail.lifecycle_reactivations ?? []).at(-1) ?? null;
  // W3 spine (#489) — the confirmed→owned boundary is 'owned' ONLY via assume_incident_command
  // (establish command) and owned→confirmed ONLY via release_incident_command. Those two
  // steps are intercepted here; every other advance/revert stays on the plain lifecycle route.
  const isAssumeStep = lc === 'confirmed' && lcNext === 'owned';
  const isReleaseStep = lc === 'owned' && lcPrev === 'confirmed';
  // W3 unified command + transfer (#492) — command-structure derived state. The controls appear
  // whenever the incident has live command (owned/response); the pending §4.8 hand-over gates
  // out the entry/transfer actions in favour of the receiving IC's acknowledgement.
  const command = detail.command;
  const hasCommand = !!command?.lead;
  const isUnified = !!command?.unified;
  const pendingTransfer = command?.pending_transfer ?? null;
  const participantIds = (command?.participants ?? []).map((p) => p.agency_id);
  const coLeadIds = (command?.participants ?? [])
    .filter((p) => !p.is_lead)
    .map((p) => p.agency_id);
  // ★ Slice-5 owner gate (feat/ownership-coordination) — a NON-OWNER cannot command another
  // organization's incident (§4.1). isOwner ⟺ the viewer's agency holds a live command
  // participation. viewerAgencyId is read at the top of the component (hook order); under AUTH-off
  // the client has NO agency (user null) → default OWNER so the single-tenant demo keeps its
  // controls; the server 403 not_lead (Slice 5) is the authoritative wall — this web hide is the
  // cosmetic companion that goes live with the #606 enforcement flip.
  const isOwner = viewerAgencyId == null || participantIds.includes(viewerAgencyId);

  // #485 SIDE-EFFECTS — a reactivation does NOT auto-restore the operational period, Incident
  // Command, or the operator Response Level (all correctly preserved-but-not-resumed). Surface a
  // cue for each unmet follow-up on a currently-open, previously-reactivated incident so the duty
  // officer isn't left to notice silently. The RL cue fires only when the pin PREDATES the reopen.
  const hasActivePeriod = periods.some((p) => p.status === 'active');
  const rlAssessedBeforeReactivation =
    detail.operator_set_response_level != null &&
    detail.operator_rl_at != null &&
    latestReactivation != null &&
    new Date(detail.operator_rl_at).getTime() <
      new Date(latestReactivation.reactivated_at).getTime();
  const reactivationCues = [
    !hasActivePeriod ? REACTIVATED_OPEN_PERIOD_CUE : null,
    !hasCommand ? REACTIVATED_REASSUME_COMMAND_CUE : null,
    rlAssessedBeforeReactivation ? REACTIVATED_RESPONSE_LEVEL_CUE : null,
  ].filter((c): c is string => c != null);
  const showReactivationFollowup = isOpen && latestReactivation != null && reactivationCues.length > 0;

  // The default start for a new period: the previous period's end (no gap), else now.
  const lastPeriod = periods.length ? periods[periods.length - 1] : null;
  const lastPeriodEnd = lastPeriod ? new Date(lastPeriod.end_at) : new Date();
  const declareDefaultStart = lastPeriodEnd > new Date() ? lastPeriodEnd : new Date();

  return (
    <section data-testid="incident-management" className="space-y-3 border-t border-slate-700/70 pt-4">
      {/* ── Block A: incident header ─────────────────────────────────── */}
      <div data-testid="incident-header">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Tracked Incident</p>
        <div className="flex items-center gap-2 mt-0.5">
          <h3 className="text-sm font-mono font-semibold text-slate-100" data-testid="incident-number">
            {detail.incident_number}
          </h3>
          <Pill style={hStyle} testid="incident-hazard-badge" title={`Hazard: ${hStyle.label}`} />
          <Pill style={sStyle} testid="incident-status-pill" title={`Incident is ${sStyle.label.toLowerCase()}`} />
          {/* W4c duty watch (#526) — a per-DESK "Watching" flag, ADJACENT to but semantically
              DISTINCT from command/ownership (watch ≠ command; council A.3). Non-exclusive. */}
          <DeskWatchControl sourceType="incident" sourceId={detail.id} variant="pill" />
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          {/* #507: an Area Command is ESTABLISHED (§5.4), never "Owned" — command
              belongs to the child sites' own IMTs (App A #2, #498). */}
          {detail.is_area_command ? AREA_COMMAND_ESTABLISHED_PREFIX : 'Owned'}{' '}
          <span title={absTime(detail.opened_at)}>{relTime(detail.opened_at)}</span>
          {detail.closed_at && (
            <>
              {' · closed '}
              <span title={absTime(detail.closed_at)}>{relTime(detail.closed_at)}</span>
            </>
          )}
        </p>
        {/* #485 HONESTY (MUST-2) — the read-time reactivation line. After a reopen closed_at is
            NULL (the file is open again), so this is the only honest record that the incident was
            ever deactivated: it is derived from the advance_incident_lifecycle ledger, never from
            closed_at. Without it the header above reads continuously-active (a HARD ❌). */}
        {latestReactivation && (
          <p
            className="text-[11px] text-amber-300/90 mt-0.5"
            data-testid="incident-reactivation-line"
          >
            <span title={absTime(latestReactivation.reactivated_at)}>
              {reactivatedGapLabel(
                relTime(latestReactivation.reactivated_at),
                relTime(latestReactivation.deactivated_at),
              )}
            </span>
          </p>
        )}
        {/* #485 SIDE-EFFECTS — the post-reactivation follow-up cues (period / command / RL are
            preserved but NOT auto-resumed). Only the unmet ones render, on an open reopened file. */}
        {showReactivationFollowup && (
          <div
            className="mt-1 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1"
            data-testid="incident-reactivation-followup"
          >
            <p className="text-[10px] uppercase tracking-wider text-amber-300/80">
              {REACTIVATED_FOLLOWUP_HEADING}
            </p>
            <ul className="mt-0.5 space-y-0.5">
              {reactivationCues.map((cue) => (
                <li key={cue} className="text-[11px] leading-snug text-slate-300">
                  • {cue}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* #508 (UX wayfinding) — a CHILD incident's one-click breadcrumb BACK to its
            Area Command parent. Renders ONLY when this incident is grouped under an Area
            Command (detail.parent present); nothing on a normal incident or the parent
            itself. Uses the shipped `#incident=<id>` in-app deep-link — the SYMMETRIC
            counterpart to the #507 parent→child rows (areaCommandChildren) — which loads
            the parent's record via App.tsx's hashchange handler. NOT selectOnCop: its
            {kind:'incident'} arm only flies to a lat/lon the AreaCommandRef payload does
            not carry, so it would be a dead click. Reuses the doctrine-locked
            AREA_COMMAND_HEADING ("Area Command", App A #2) — no new operator vocabulary. */}
        {detail.parent && (
          <a
            href={`#incident=${detail.parent.id}`}
            data-testid="incident-area-command-parent-chip"
            title={`Open ${detail.parent.name}`}
            className="mt-1 inline-flex max-w-full items-center gap-1 rounded border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[11px] text-indigo-200 hover:bg-indigo-500/20"
          >
            <span aria-hidden>↑</span>
            <span className="truncate">
              {AREA_COMMAND_HEADING} · {detail.parent.name}
            </span>
          </a>
        )}
        {/* W3 spine (#489) — the structured Incident Command read: Lead Organization +
            command officer (single command). Renders only when the incident is owned.
            NEVER on an Area Command parent (R5 Slice 3 #507): App A #2 — area command
            is a FUNCTION of the participating organizations; the parent surface carries
            no person field (Ontario titles no "Area Commander", #498). Each child keeps
            its own Incident Command block on its own panel. */}
        {!detail.is_area_command && <IncidentCommandBlock command={detail.command} />}
        {/* #445-ws2 — the incident's Response Level (§5 Table 1): the operative (effective)
            level the IC asserts + the annotated system-derived (never overwritten) + the
            de-escalation divergence guard + the IC's Set/Escalate/De-escalate control (§6.1).
            NEVER on an Area Command parent — §4.6 oversight has no single IC / single level
            (the set route 409s on an AC parent; #510 no-command guard). */}
        {!detail.is_area_command && (
          <IncidentResponseLevelBlock
            rl={detail.response_level}
            hasCommand={hasCommand}
            canControl={isOwner}
            incidentId={detail.id}
            sessionId={sessionId}
            busy={busy}
            run={run}
          />
        )}
        {/* W3 unified command + transfer (#492) — the §4.7/§4.8 command-structure controls. Shown
            once the incident has live command. A pending hand-over surfaces the receiving IC's
            "Acknowledge Transfer of Command" instead of the entry/transfer affordances. */}
        {!detail.is_area_command && hasCommand && isOwner && (
          <div className="mt-1.5" data-testid="command-controls">
            {pendingTransfer ? (
              // #481 MUST-2 — the accept affordance is kind-aware: a §4.8 transfer is
              // "Acknowledge Transfer of Command"; a §4.7 unified invite is "Accept Unified
              // Command". Both call the shipped acknowledge-transfer route (the server branches).
              <button
                type="button"
                data-testid="acknowledge-transfer"
                data-kind={pendingTransfer.kind}
                disabled={busy}
                onClick={() => void run(() => acknowledgeIncidentTransfer(detail.id, sessionId))}
                className="text-[11px] px-2 py-1 rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-medium"
              >
                {pendingTransfer.kind === 'unified_invite'
                  ? ACCEPT_UNIFIED_COMMAND_LABEL
                  : ACKNOWLEDGE_TRANSFER_LABEL}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid="enter-unified-command-open"
                  disabled={busy || unifiedOpen}
                  onClick={() => {
                    setTransferOpen(false);
                    setUnifiedOpen(true);
                  }}
                  className="text-[11px] px-2 py-1 rounded border border-sky-500/60 text-sky-300 hover:bg-sky-600/20 disabled:opacity-50"
                >
                  {INVITE_TO_UNIFIED_COMMAND_LABEL}
                </button>
                <button
                  type="button"
                  data-testid="transfer-command-open"
                  disabled={busy || transferOpen}
                  onClick={() => {
                    setUnifiedOpen(false);
                    setTransferOpen(true);
                  }}
                  className="text-[11px] px-2 py-1 rounded border border-amber-500/60 text-amber-300 hover:bg-amber-600/20 disabled:opacity-50"
                >
                  {TRANSFER_OF_COMMAND_LABEL}
                </button>
                {isUnified && (
                  <button
                    type="button"
                    data-testid="return-to-single-command"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        // §4.7 return to single command — every co-lead leaves, one lead remains.
                        for (const agencyId of coLeadIds) {
                          await releaseIncidentCommand(detail.id, sessionId, agencyId);
                        }
                      })
                    }
                    className="text-[11px] px-2 py-1 rounded border border-slate-500/60 text-slate-300 hover:bg-slate-700/40 disabled:opacity-50"
                  >
                    {RETURN_TO_SINGLE_COMMAND_LABEL}
                  </button>
                )}
              </div>
            )}
            {unifiedOpen && !pendingTransfer && (
              <InviteToUnifiedCommandForm
                busy={busy}
                excludeAgencyIds={participantIds}
                onCancel={() => setUnifiedOpen(false)}
                onInvite={(input) =>
                  void run(() => inviteToUnifiedCommand(detail.id, input, sessionId)).then(() =>
                    setUnifiedOpen(false),
                  )
                }
              />
            )}
            {transferOpen && !pendingTransfer && (
              <TransferOfCommandForm
                busy={busy}
                excludeAgencyIds={participantIds}
                onCancel={() => setTransferOpen(false)}
                onTransfer={(input) =>
                  void run(() => transferIncidentCommand(detail.id, input, sessionId)).then(() =>
                    setTransferOpen(false),
                  )
                }
              />
            )}
          </div>
        )}
        {/* R1/#477 — incident lifecycle phase + the BIDIRECTIONAL advance/revert controls.
            The phase DRIVES the source marker's symbology. Advance steps one phase forward;
            Revert de-escalates one (and REACTIVATES a 'deactivated' incident back to the last
            un-commanded phase 'confirmed', reopening the file; W7/#535). Both behind the same
            two-step confirm as Close. */}
        <div className="mt-2" data-testid="incident-lifecycle">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Phase</span>
            <Pill
              style={lcStyle}
              testid="incident-lifecycle-pill"
              title={`Lifecycle phase: ${lcStyle.label}`}
            />
          </div>
          {confirmLifecycle ? (
            <div className="mt-1 space-y-1" data-testid="lifecycle-confirm">
              {/* #485 STALENESS (MUST-3) — a WARNING (not a block) when reopening an incident
                  deactivated longer than the founder threshold: same-hazard re-emergence only,
                  else OWN a new incident. Operator judgment governs (confirm still available). */}
              {isStaleReactivation && confirmLifecycle.direction === 'revert' && (
                <p
                  data-testid="stale-reactivation-warning"
                  className="text-[11px] leading-snug text-amber-300"
                >
                  {staleReactivationWarning(daysSinceDeactivation)}
                </p>
              )}
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-300">
                  {confirmLifecycle.direction === 'advance'
                    ? 'Advance'
                    : isReactivate
                      ? REACTIVATE_INCIDENT_LABEL
                      : 'Revert'}{' '}
                  to {incidentLifecycleStyle(confirmLifecycle.to).label}?
                </span>
                <button
                  type="button"
                  data-testid="lifecycle-confirm-yes"
                  disabled={busy}
                  onClick={() =>
                    void run(() =>
                      advanceIncidentLifecycle(detail.id, confirmLifecycle.to, sessionId),
                    ).then(() => setConfirmLifecycle(null))
                  }
                  className="text-[11px] px-2 py-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-medium"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmLifecycle(null)}
                  className="text-[11px] px-2 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            (lcNext || lcPrev) && (
              <div className="mt-1 space-y-1.5">
                <div className="flex items-center gap-2">
                  {lcNext &&
                    (isAssumeStep ? (
                      // R5 Slice 3 (#507): an Area Command parent NEVER offers Assume
                      // Incident Command — command (a person) belongs to the child
                      // sites' own IMTs (App A #2; no titled area-command lead, #498).
                      detail.is_area_command ? null : (
                        <button
                          type="button"
                          data-testid="assume-command-open"
                          disabled={busy || assumeOpen}
                          onClick={() => setAssumeOpen(true)}
                          className="text-[11px] px-2 py-1 rounded border border-sky-500/60 text-sky-300 hover:bg-sky-600/20 disabled:opacity-50"
                        >
                          {ASSUME_COMMAND_LABEL}
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        data-testid="lifecycle-advance"
                        disabled={busy}
                        onClick={() => setConfirmLifecycle({ to: lcNext, direction: 'advance' })}
                        className="text-[11px] px-2 py-1 rounded border border-sky-500/60 text-sky-300 hover:bg-sky-600/20 disabled:opacity-50"
                      >
                        Advance → {incidentLifecycleStyle(lcNext).label}
                      </button>
                    ))}
                  {lcPrev &&
                    (isReleaseStep ? (
                      // #507: Release Incident Command is a command (person) action —
                      // never offered on an Area Command parent (App A #2, #498).
                      detail.is_area_command ? null : (
                        <button
                          type="button"
                          data-testid="release-command"
                          // ★ Slice-5 — only the owner may relinquish command (§4.1); the server 403s a non-owner.
                          disabled={busy || confirmRelease || !isOwner}
                          onClick={() => setConfirmRelease(true)}
                          className="text-[11px] px-2 py-1 rounded border border-amber-500/60 text-amber-300 hover:bg-amber-600/20 disabled:opacity-50"
                        >
                          {RELEASE_COMMAND_LABEL}
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        data-testid="lifecycle-revert"
                        disabled={busy}
                        onClick={() => setConfirmLifecycle({ to: lcPrev, direction: 'revert' })}
                        className="text-[11px] px-2 py-1 rounded border border-slate-500/60 text-slate-300 hover:bg-slate-700/40 disabled:opacity-50"
                      >
                        {isReactivate ? REACTIVATE_INCIDENT_LABEL : 'Revert'} ←{' '}
                        {incidentLifecycleStyle(lcPrev).label}
                      </button>
                    ))}
                </div>
                {/* #507: the render condition carries its OWN is_area_command gate (not
                    just the open button) — stale assumeOpen state from another incident
                    must never render a person-field form on an Area Command parent. */}
                {isAssumeStep && assumeOpen && !detail.is_area_command && (
                  <AssumeCommandForm
                    busy={busy}
                    onCancel={() => setAssumeOpen(false)}
                    onAssume={(input) =>
                      void run(() => assumeIncidentCommand(detail.id, input, sessionId)).then(() =>
                        setAssumeOpen(false),
                      )
                    }
                  />
                )}
                {isReleaseStep && confirmRelease && !detail.is_area_command && (
                  <div className="flex items-center gap-2" data-testid="release-command-confirm">
                    <span className="text-[11px] text-slate-300">
                      Relinquish Incident Command? (reverts to Confirmed)
                    </span>
                    <button
                      type="button"
                      data-testid="release-command-yes"
                      disabled={busy}
                      onClick={() =>
                        void run(() => releaseIncidentCommand(detail.id, sessionId)).then(() =>
                          setConfirmRelease(false),
                        )
                      }
                      className="text-[11px] px-2 py-1 rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-medium"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRelease(false)}
                      className="text-[11px] px-2 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )
          )}
        </div>
        {/* #183 corroboration v1 — contextual, renders only when N > 0. */}
        <IncidentCorroborationChip
          lon={detail.lon}
          lat={detail.lat}
          incidentNumber={detail.incident_number}
        />
        {/* #180 — AAR time replay (ambient record-keeping: a read-only scrub of
            recorded history; available for open AND closed incidents). */}
        {onOpenReplay && (
          <button
            type="button"
            data-testid="open-aar-replay"
            disabled={replayActive}
            onClick={() => onOpenReplay(detail.id)}
            className="mt-2 text-[11px] px-2 py-1 rounded border border-amber-500/60 text-amber-300 hover:bg-amber-600/20 disabled:opacity-50"
            title="Scrub this incident's recorded history on the map (read-only)"
          >
            ⏪ AAR Replay
          </button>
        )}
        {/* #612 — After-Action Report: the closing beat of the incident workflow (§8). Mounts
            ONLY when the incident is deactivated — a live incident may not emit a closing doc. */}
        {detail.lifecycle_state === 'deactivated' && (
          <AfterActionReportPanel incidentId={detail.id} />
        )}
        {isOpen && (
          <div className="mt-2">
            {confirmClose ? (
              <div className="flex items-center gap-2" data-testid="close-incident-confirm">
                <span className="text-[11px] text-slate-300">Deactivate this incident?</span>
                <button
                  type="button"
                  data-testid="close-incident-yes"
                  disabled={busy}
                  onClick={() =>
                    void run(() => closeIncident(detail.id, sessionId)).then(() => setConfirmClose(false))
                  }
                  className="text-[11px] px-2 py-1 rounded bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-medium"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmClose(false)}
                  className="text-[11px] px-2 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="close-incident"
                // ★ Slice-5 — deactivating a COMMANDED incident is a command act; only the owner may
                // (the server 403s a non-owner). A pre-command incident stays closeable by its tenant.
                disabled={hasCommand && !isOwner}
                onClick={() => setConfirmClose(true)}
                className="text-[11px] px-2 py-1 rounded border border-rose-500/60 text-rose-300 hover:bg-rose-600/20 disabled:opacity-50"
              >
                Deactivate Incident
              </button>
            )}
          </div>
        )}
      </div>

      {/* R5 Area Command Slice 2 (#494) — membership visibility (§4.6 oversight). A CHILD
          shows the "Under Area Command — <name>" badge + a reversible Remove; an Area
          Command PARENT shows its member list (the shipped children[]) + a per-child Remove.
          Ontario IMS §4.6 names NO single titled "area commander" (#498) — oversight is held
          by the member incidents' own Incident Command blocks (Lead Organization + Incident
          Commander, §4.5/§4.1). */}
      {detail.parent && (
        <div
          data-testid="incident-area-command-membership"
          className="rounded border border-indigo-500/40 bg-indigo-500/5 px-3 py-2"
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className="min-w-0 flex-1 truncate text-[11px] font-medium text-indigo-200"
              data-testid="under-area-command-badge"
            >
              {underAreaCommandLabel(detail.parent.name)}
            </span>
            <button
              type="button"
              data-testid="remove-from-area-command"
              disabled={busy}
              onClick={() => void run(() => removeFromAreaCommand(detail.id, sessionId))}
              className="shrink-0 text-[11px] px-2 py-1 rounded border border-slate-500/60 text-slate-300 hover:bg-slate-700/40 disabled:opacity-50"
            >
              {REMOVE_FROM_AREA_COMMAND_LABEL}
            </button>
          </div>
          <p className="mt-0.5 font-mono text-[10px] text-slate-500">
            {detail.parent.incident_number}
          </p>
        </div>
      )}
      {/* R5 Slice 3 (#507) — the Area Command parent's expand panel: heading gate-
          corrected to the verbatim §4.6 "Incidents under area command (N)" (children
          are App A #35 incidents, never "members"); child rows render the child's OWN
          name + incident_number only. Extracted so areaCommandChildren.lock.test.tsx
          pins the rendered surface with sentinel children (#497 pattern). */}
      {detail.is_area_command && (
        <>
          <AreaCommandChildrenSection
            childIncidents={detail.children}
            busy={busy}
            onRemove={(childId) => void run(() => removeFromAreaCommand(childId, sessionId))}
          />
          {/* R5 Slice 5 (#541) — the Area Command's OWN broad objectives + critical-resource-
              sharing priorities (§4.6 / App A #2). The WHY (its own directives) above the WHAT
              (the roster). Save PUTs the whole ordered list (replace-whole-ordered-list). */}
          <AreaCommandDirectivesSection
            objectives={detail.area_command_objectives ?? []}
            resourcePriorities={detail.area_command_resource_priorities ?? []}
            busy={busy}
            onSaveObjectives={(list) =>
              void run(() => setAreaCommandObjectives(detail.id, list, sessionId))
            }
            onSaveResourcePriorities={(list) =>
              void run(() => setAreaCommandResourcePriorities(detail.id, list, sessionId))
            }
          />
          {/* R5 Slice 4 (#514) — generate the Area-Command-scoped Situation Report: the
              aggregate roll-up of the grouped incidents (opens in the shared SITREP viewer). */}
          <button
            type="button"
            data-testid="generate-area-command-sitrep"
            disabled={busy}
            onClick={generateAreaCommandSitrep}
            className="w-full text-[11px] py-1.5 rounded border border-emerald-500/60 text-emerald-300 hover:bg-emerald-600/20 disabled:opacity-50"
          >
            {busy ? 'Generating…' : 'Generate Situation Report'}
          </button>
        </>
      )}

      {/* #206 — evacuation orders covering any community linked to this incident
          (renders only when at least one active order does). */}
      <ConnectedEvacOrders source={{ kind: 'incident', id: incidentId }} />

      {/* #425 LINK — assistance requests attached to this incident (read-only): the
          request that started it + its routing/forwarding history + issued down-tasks.
          Closes the loop the Liaison persona flagged — declaring off the anomaly no
          longer orphans the inbound request. */}
      {linkedRequests.length > 0 && (
        <div data-testid="incident-linked-requests" className="space-y-2">
          <h4 className="text-[10px] uppercase tracking-wider text-slate-400">
            Linked Assistance Requests · {linkedRequests.length}
          </h4>
          {linkedRequests.map((r) => (
            <div
              key={r.id}
              data-testid="incident-linked-request"
              className="rounded border border-slate-700 bg-slate-900/40 p-2 space-y-1"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-slate-100">{r.requestor_name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{r.status}</span>
              </div>
              <p className="text-[10px] text-slate-400">
                {r.urgency} · {r.requestor_type.replace(/_/g, ' ')}
                {r.routed_to ? ` · routed → ${r.routed_to}` : ''}
                {r.forwarded_to ? ` · forwarded → ${r.forwarded_to}` : ''}
              </p>
              {r.assigned_resource_request && (
                <p className="text-[10px] text-slate-400">
                  resource {r.assigned_resource_request.request_number} ·{' '}
                  {r.assigned_resource_request.request_status.replace(/_/g, ' ')}
                </p>
              )}
              {r.down_tasks && r.down_tasks.length > 0 && (
                <ul className="text-[10px] text-slate-400 space-y-0.5 pt-0.5">
                  {r.down_tasks.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-2">
                      <span className="font-mono">
                        {t.task_number} → {t.target_agency_name ?? 'agency'}
                      </span>
                      <span className="uppercase tracking-wider">
                        {t.task_status.replace(/_/g, ' ')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── #427c: contributing hazards (HAZARD COMPOSITION) ────────────
          Which hazard FRONTS contribute to this incident, at what contributing SEVERITY
          (type · severity). This is NOT a per-hazard Response Level — Ontario IMS §5
          Table 1 gives the INCIDENT one Response Level (the header max-fold). Evacuations
          are protective actions (below), not hazards, so they are excluded here. */}
      {detail && <ContributingHazardsSection hazards={detail.contributing_hazards} />}

      {/* ── R2/#478: protective actions + coverage-read ─────────────────
          Evac orders / life-safety CAPs collapsed INTO this incident (auto-linked by
          proximity OR operator-linked), and the communities their zones cover (the
          Municipal EOC "my community is in-zone" read). Evacuation = a protective action
          (Ontario IMS); an evac ORDER is "Declared by" its issuing authority (the COP
          records, never confers). */}
      {detail &&
        (detail.protective_actions.length > 0 || detail.in_zone_communities.length > 0) && (
          <div data-testid="incident-protective-actions" className="space-y-2">
            <h4 className="text-[10px] uppercase tracking-wider text-slate-400">Protective Actions</h4>
            {detail.protective_actions.length === 0 ? (
              <p className="text-[11px] text-slate-500">No linked evacuations.</p>
            ) : (
              <ul className="space-y-1.5">
                {detail.protective_actions.map((pa) => (
                  <li
                    key={`${pa.kind}:${pa.id}`}
                    data-testid="protective-action-row"
                    className="rounded border border-violet-800/50 bg-violet-950/20 px-2 py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-100">{pa.label}</span>
                      {pa.level && (
                        <span className="text-[10px] uppercase tracking-wide text-violet-300">
                          {pa.level}
                        </span>
                      )}
                      <span className="ml-auto text-[10px] rounded bg-slate-700/70 px-1.5 py-0.5 text-slate-300">
                        {pa.link_mode === 'auto' ? 'auto-linked' : 'linked'}
                      </span>
                    </div>
                    {pa.issuing_authority && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Declared by {pa.issuing_authority}
                      </p>
                    )}
                    {/* #458 — coordinator (Far-North tripartite case): SECONDARY, below +
                        dimmer than the issuer, never above it (§4.2 coordination ≠ command). */}
                    {pa.coordinating_authority && (
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {COORDINATED_BY_LABEL} {pa.coordinating_authority}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {detail.in_zone_communities.length > 0 && (
              <div data-testid="incident-in-zone-communities" className="pt-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Communities in zone
                </p>
                <ul className="flex flex-wrap gap-1">
                  {detail.in_zone_communities.map((c) => (
                    <li
                      key={c.id}
                      data-testid="in-zone-community"
                      className="text-[11px] rounded bg-slate-800 border border-slate-600 px-1.5 py-0.5 text-slate-200"
                      title={`${c.community_type} · coverage: ${c.coverage === 'service_area' ? 'service area' : 'centroid'}`}
                    >
                      {c.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

      {/* ── Block B: operational-period strip ────────────────────────── */}
      <div data-testid="operational-period-strip" className="space-y-2">
        <h4 className="text-[10px] uppercase tracking-wider text-slate-400">Operational Periods</h4>

        {currentPeriod ? (
          <div data-testid="current-period-card" className="rounded border border-slate-700 bg-slate-900/40 p-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-100">Period {currentPeriod.period_number}</span>
              <Pill style={periodStatusStyle(currentPeriod.status)} testid="current-period-status" />
            </div>
            <p className="text-[10px] text-slate-400">
              <span title={absTime(currentPeriod.start_at)}>{absTime(currentPeriod.start_at)}</span>
              {' → '}
              <span title={absTime(currentPeriod.end_at)}>{absTime(currentPeriod.end_at)}</span>
            </p>
            <div className="flex items-center gap-1.5" data-testid="current-period-cadence">
              <span className="text-[10px] text-slate-500">
                {cadenceLabel(currentPeriod.sitrep_cadence_minutes)}
              </span>
              {currentPeriod.status !== 'closed' && (
                <select
                  data-testid="current-period-cadence-select"
                  aria-label="Change SITREP cadence"
                  disabled={busy}
                  value={currentPeriod.sitrep_cadence_minutes == null ? '' : String(currentPeriod.sitrep_cadence_minutes)}
                  onChange={(e) =>
                    void run(() =>
                      setOperationalPeriodCadence(
                        currentPeriod.id,
                        e.target.value === '' ? null : Number(e.target.value),
                        sessionId,
                      ),
                    )
                  }
                  className="bg-slate-900 border border-slate-700 rounded text-[10px] text-slate-300 px-1 py-0.5"
                >
                  {CADENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {/* Forward-only transition (act register) */}
            {currentPeriod.status === 'planned' && (
              <button
                type="button"
                data-testid="period-activate"
                disabled={busy}
                onClick={() => void run(() => transitionOperationalPeriod(currentPeriod.id, 'active', sessionId))}
                className="text-[11px] px-2 py-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-medium"
              >
                Activate period
              </button>
            )}
            {currentPeriod.status === 'active' && (
              <button
                type="button"
                data-testid="period-close"
                disabled={busy}
                onClick={() => void run(() => transitionOperationalPeriod(currentPeriod.id, 'closed', sessionId))}
                className="text-[11px] px-2 py-1 rounded border border-slate-500 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
              >
                Close period
              </button>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-slate-500" data-testid="no-periods">
            No operational period opened yet.
          </p>
        )}

        {/* History stepper (ambient) — only when 2+ periods exist. */}
        {periods.length > 1 && (
          <div className="pt-1" title="Operational-period heartbeat">
            <LifecycleStepper steps={periodSteps} data-testid="period-history-stepper" />
          </div>
        )}

        {/* Open next period (act register) */}
        {isOpen &&
          (declaring ? (
            <DeclarePeriodForm
              defaultStart={declareDefaultStart}
              busy={busy}
              onCancel={() => setDeclaring(false)}
              onSubmit={(startIso, endIso, cadence) =>
                void run(() =>
                  declareOperationalPeriod(detail.id, {
                    start_at: startIso,
                    end_at: endIso,
                    sitrep_cadence_minutes: cadence,
                    session_id: sessionId,
                  }),
                ).then(() => setDeclaring(false))
              }
            />
          ) : (
            <button
              type="button"
              data-testid="declare-next-period"
              onClick={() => setDeclaring(true)}
              className="w-full text-[11px] py-1.5 rounded border border-sky-500/60 text-sky-300 hover:bg-sky-600/20"
            >
              Open Next Period
            </button>
          ))}
      </div>

      {/* ── Block C: report chain ────────────────────────────────────── */}
      <div data-testid="report-chain" className="space-y-1">
        <h4 className="text-[10px] uppercase tracking-wider text-slate-400">Situation Reports</h4>
        {chainRows.length === 0 ? (
          <p className="text-[11px] text-slate-500">No SITREPs issued for this incident yet.</p>
        ) : (
          <div className="space-y-0.5">
            {chainRows.map((row) => (
              <ReportChainRow key={row.ref.id} report={row.ref} label={row.label} onOpen={onOpenSitrep} />
            ))}
          </div>
        )}
      </div>

      {/* ── Block D: Public-Information draft (#300) ──────────────────────
          The "share"-phase action — draft the §4.19 Public-Information paragraph
          from the wire, grounded + cited, as an editable candidate the operator
          owns and folds in. Writes no SITREP; never auto-forwards. */}
      <PublicInfoDraft incidentId={incidentId} />

      {/* ── Blocks E + F: Emergency Declaration (#495) + Public Alert Request (#497) ──
          PARALLEL SIBLINGS to command + evacuation — the declaration-of-emergency record +
          "Notify the PEOC" (#490 bridge), and a public-alert REQUEST routed TO the PEOC
          (the only issuer, PERP §6). Both are GATED OFF an Area Command parent (#511):
          declarations + public-alert requests are jurisdictional acts that belong on a
          MEMBER incident, not the §4.6 oversight entity — the Area Command develops broad
          objectives + resource priorities (App A #2), it does not itself declare. */}
      {!detail.is_area_command ? (
        <>
          <EmergencyDeclarationBlock incidentId={incidentId} hazardType={detail.hazard_type} />
          <PublicAlertRequestBlock
            incidentId={incidentId}
            incidentNumber={detail.incident_number}
            hazardType={detail.hazard_type}
          />
          {/* ── Block G: Resident Public-Information release (#450) ──────────
              The municipal EIO publishes a §7.10 public-information release to the
              community's OWN opted-in residents — the municipality ISSUES its own
              release (NOT Alert Ready, which it REQUESTS via the block above). A
              jurisdictional act → a MEMBER incident, gated off Area Command like its
              siblings. Targets the incident's in-zone communities. */}
          <ResidentPublicInfoBlock
            incidentId={incidentId}
            hazardType={detail.hazard_type}
            inZoneCommunities={detail.in_zone_communities}
          />
        </>
      ) : (
        <p
          data-testid="area-command-declaration-pointer"
          className="text-[10px] text-slate-500"
        >
          Declarations and public-alert requests are recorded on the incidents under area command.
        </p>
      )}

      {err && <p className="text-[10px] text-rose-400" data-testid="incident-management-error">{err}</p>}
    </section>
  );
}
